/**
 * Fuzzy speaker-name autocomplete. Case-insensitive substring first, then
 * Levenshtein-lite tolerance so "Zenwirth" still matches "Zehnwirth" and
 * "Bentzion" still finds "Benzion". Uses the shared /api/speakers cache
 * populated by useSpeakerPortrait — no extra network call per keystroke.
 *
 * Feb-2026 (user): "Autocomplete Everywhere: Reuse the fuzzy autocomplete
 * in the Portrait Crop Studio + admin panel so you never type a full name
 * again." — this component is the shared implementation.
 */
import React from 'react';
import { getSpeakers } from '../services/api';

const normalizeName = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '');

const levDist = (a, b, cap = 4) => {
  const m = a.length, n = b.length;
  if (Math.abs(m - n) > cap) return cap + 1;
  if (!m) return n; if (!n) return m;
  let prev = Array(n + 1).fill(0);
  for (let j = 0; j <= n; j++) prev[j] = j;
  for (let i = 1; i <= m; i++) {
    const curr = Array(n + 1).fill(0);
    curr[0] = i;
    let rowMin = i;
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
      if (curr[j] < rowMin) rowMin = curr[j];
    }
    if (rowMin > cap) return cap + 1;
    prev = curr;
  }
  return prev[n];
};

const SpeakerNameAutocomplete = ({
  value,
  onChange,
  onPick,
  placeholder,
  disabled,
  testid = 'speaker-name-input',
  className,
  darkTheme = false,
}) => {
  const [names, setNames] = React.useState(() => {
    try {
      const raw = localStorage.getItem('vaad_speakers_map_v3');
      if (!raw) return [];
      return Object.keys(JSON.parse(raw) || {});
    } catch { return []; }
  });
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => {
    getSpeakers()
      .then((rows) => setNames((rows || []).map((r) => r?.name).filter(Boolean)))
      .catch(() => {});
  }, []);
  const suggestions = React.useMemo(() => {
    const q = (value || '').trim();
    if (q.length < 1) return [];
    const qN = normalizeName(q);
    const exact = [];
    const startsWith = [];
    const contains = [];
    const fuzzy = [];
    for (const n of names) {
      const nN = normalizeName(n);
      if (nN === qN) exact.push(n);
      else if (nN.startsWith(qN)) startsWith.push(n);
      else if (nN.includes(qN)) contains.push(n);
      else if (qN.length >= 4) {
        const cap = Math.max(3, Math.floor(qN.length / 5));
        if (Math.abs(nN.length - qN.length) <= cap + 2) {
          if (levDist(qN, nN, cap) <= cap) fuzzy.push(n);
        }
      }
    }
    return [...exact, ...startsWith, ...contains, ...fuzzy].slice(0, 6);
  }, [value, names]);

  const light = !darkTheme;
  const inputCls = className ?? (light
    ? 'w-full px-3 py-2 text-sm rounded-md bg-white border border-[#e1ddd6] text-[#0f1729] placeholder:text-[#8a8f99] focus:outline-none focus:border-[#9a7b24]'
    : 'w-full px-2 py-1.5 text-[11px] rounded-md bg-white/5 border border-white/10 text-white placeholder:text-white/40 focus:outline-none focus:border-[#e5c976]/60');
  const listCls = light
    ? 'absolute z-50 left-0 right-0 top-full mt-1 max-h-52 overflow-auto rounded-md bg-white border border-[#e1ddd6] shadow-xl divide-y divide-[#e1ddd6]'
    : 'absolute z-50 left-0 right-0 top-full mt-1 max-h-52 overflow-auto rounded-md bg-[#132043] border border-white/10 shadow-xl divide-y divide-white/5';
  const optCls = light
    ? 'w-full text-left px-3 py-1.5 text-sm text-[#0f1729] hover:bg-[#faf7ee]'
    : 'w-full text-left px-2 py-1.5 text-[11px] text-white hover:bg-[#e5c976]/15';

  return (
    <div className="relative">
      <input
        type="text"
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && suggestions[0]) {
            e.preventDefault();
            onPick(suggestions[0]);
            setOpen(false);
          } else if (e.key === 'Escape') { setOpen(false); }
        }}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        spellCheck={false}
        className={inputCls}
        data-testid={testid}
      />
      {open && suggestions.length > 0 && (
        <ul className={listCls} data-testid={`${testid}-suggestions`}>
          {suggestions.map((n) => (
            <li key={n}>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); onPick(n); setOpen(false); }}
                className={optCls}
                data-testid={`${testid}-option`}
              >{n}</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SpeakerNameAutocomplete;
